import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { BSLEmploymentStatus } from '@cbo/shared-library';
import useLaborQueryKeys from './queryKeys';

import {
  UpdateEmployeeContactInfoRequestSchema,
  ChangeJobActivationStatusDto,
  UpdateEmployeeCertificationSchema,
  UpdateEmployeeDetailsSchema,
  UpdateEmployeePersonalInfoRequestSchema,
  UpdateJobDto,
  UpdateResponse,
  UpdateEmployeeCertificationInfoSchema,
  EmployeeContactDetailsInformation,
  EmploymentHistoryIdResponse,
  UpdateEmployeeRequestSchema,
  UpdateEmployeeHistorySchema,
  JobProfile,
  UpdateJobProfileDto,
  CreateSelfEmployeeRequestSchema,
  CreateSelfEmployeeResponse,
  UpdateUserStatusResponse,
  CreateEmployeeResponse,
  CreateEmployeeRequestSchema,
  ExportFileReportGridResponse,
  EmployeePayrollDetailExportDto,
  TipShareDistributionConfig,
  TipShareContributionConfig,
  SaveTipDistributionDto,
  SaveTipDistributionResponseDto,
  CalculateTipDistributionRequestDto,
  CalculateTipDistributionResponseDto,
  CreateTipShareShiftDto,
  UpdateTipShareShiftDto,
  TipShareShift,
  DismissBannerDto,
  DismissBannerResponseDto,
  BannerStateViewDto,
  FinalizationRequestDto,
  FinalizationResponseDto,
} from '../types';
import useLaborRequests from './index';
import { IApiError } from '../../services/types';
import { BaseReportDataRequest } from '../../../api/sales/requests';

export const useUpdateContactInformationMutation = (): UseMutationResult<
  UpdateResponse | void,
  unknown,
  { employeeId: string; contactInfo: EmployeeContactDetailsInformation }
> => {
  const { updateContactInformation } = useLaborRequests();
  return useMutation(
    (updatePersonalInfoVariables: { employeeId: string; contactInfo: EmployeeContactDetailsInformation }) =>
      updateContactInformation(updatePersonalInfoVariables.employeeId, updatePersonalInfoVariables.contactInfo),
    {
      useErrorBoundary: false,
    }
  );
};

export const useUpdateEmployeeDetailsMutation = (): UseMutationResult<
  UpdateResponse | void,
  unknown,
  { employeeId: string; employeeDetails: UpdateEmployeeDetailsSchema }
> => {
  const { updateEmployeeDetails } = useLaborRequests();
  return useMutation(
    (updateEmployeeBirthdateVariables: { employeeId: string; employeeDetails: UpdateEmployeeDetailsSchema }) =>
      updateEmployeeDetails(
        updateEmployeeBirthdateVariables.employeeId,
        updateEmployeeBirthdateVariables.employeeDetails
      ),
    {
      useErrorBoundary: true,
    }
  );
};

export const useUpdateEmployeeCertificationMutation = (
  employeeId: string,
  certificationId?: string | null
): UseMutationResult<UpdateResponse | void, unknown, UpdateEmployeeCertificationInfoSchema> => {
  const { updateEmployeeCertification, createEmployeeCertification } = useLaborRequests();
  return useMutation(
    (certification: UpdateEmployeeCertificationInfoSchema) =>
      certificationId
        ? updateEmployeeCertification(employeeId, certificationId, certification)
        : createEmployeeCertification(employeeId, certification),
    {
      useErrorBoundary: true,
    }
  );
};

export const useUpdateEmployeeMutation = (
  employeeId: string
): UseMutationResult<UpdateResponse | void, unknown, UpdateEmployeeRequestSchema> => {
  const { updateEmployee } = useLaborRequests();

  return useMutation(
    (updateEmployeeVariables: UpdateEmployeeRequestSchema) => updateEmployee(employeeId, updateEmployeeVariables),
    { useErrorBoundary: false }
  );
};

export const useCreateEmploymentHistoryMutation = (): UseMutationResult<
  EmploymentHistoryIdResponse | void,
  unknown,
  { employeeId: string; employmentHistory: UpdateEmployeeHistorySchema }
> => {
  const { createEmploymentHistory } = useLaborRequests();
  return useMutation(
    (createEmploymentHistoryPayload: { employeeId: string; employmentHistory: UpdateEmployeeHistorySchema }) =>
      createEmploymentHistory(
        createEmploymentHistoryPayload.employeeId,
        createEmploymentHistoryPayload.employmentHistory
      )
  );
};

export const useUpdateEmploymentHistoryMutation = (): UseMutationResult<
  void,
  unknown,
  { employeeId: string; employmentHistoryId: string; employmentHistory: UpdateEmployeeHistorySchema }
> => {
  const { updateEmploymentHistory } = useLaborRequests();
  return useMutation(
    (updateEmploymentHistoryPayload: {
      employeeId: string;
      employmentHistoryId: string;
      employmentHistory: UpdateEmployeeHistorySchema;
    }) =>
      updateEmploymentHistory(
        updateEmploymentHistoryPayload.employeeId,
        updateEmploymentHistoryPayload.employmentHistoryId,
        updateEmploymentHistoryPayload.employmentHistory
      )
  );
};

export const useUpdateSelfEmployeeInformationMutation = (): UseMutationResult<
  UpdateResponse | void,
  unknown,
  UpdateEmployeeContactInfoRequestSchema | UpdateEmployeePersonalInfoRequestSchema
> => {
  const { updateSelfEmployeeInfo } = useLaborRequests();
  return useMutation(
    (employeeInformation: UpdateEmployeeContactInfoRequestSchema | UpdateEmployeePersonalInfoRequestSchema) =>
      updateSelfEmployeeInfo(employeeInformation),
    { useErrorBoundary: false }
  );
};

export const useUpdateSelfEmployeeCertificationMutation = (
  certificateId: string
): UseMutationResult<UpdateResponse | void, unknown, UpdateEmployeeCertificationInfoSchema> => {
  const { updateSelfEmployeeCertification, createSelfEmployeeCertification } = useLaborRequests();
  return useMutation(
    (certification: UpdateEmployeeCertificationSchema) =>
      certificateId
        ? updateSelfEmployeeCertification(certification, certificateId)
        : createSelfEmployeeCertification(certification),
    {
      useErrorBoundary: true,
    }
  );
};

export const useUpdateEmployeeUserStatusMutation = (): UseMutationResult<
  UpdateUserStatusResponse | void,
  unknown,
  { username: string; status: BSLEmploymentStatus }
> => {
  const { updateUserStatus } = useLaborRequests();
  return useMutation((updateUserStatusVariables: { username: string; status: BSLEmploymentStatus }) =>
    updateUserStatus(updateUserStatusVariables.username, updateUserStatusVariables.status)
  );
};

export const useUpdateJobMutation = (): UseMutationResult<
  void,
  unknown,
  { jobId: string; updateJobDto: UpdateJobDto },
  unknown
> => {
  const { updateJob } = useLaborRequests();

  return useMutation(
    (updateJobcodeVariables: { jobId: string; updateJobDto: UpdateJobDto }) =>
      updateJob(updateJobcodeVariables.jobId, updateJobcodeVariables.updateJobDto),
    {
      useErrorBoundary: true,
    }
  );
};

export const useChangeJobActivationStatusMutation = (): UseMutationResult<
  void,
  unknown,
  { jobId: string; changeJobActivationDto: ChangeJobActivationStatusDto },
  unknown
> => {
  const { changeJobActivation } = useLaborRequests();

  return useMutation(
    (changeJobActivationVariables: { jobId: string; changeJobActivationDto: ChangeJobActivationStatusDto }) =>
      changeJobActivation(changeJobActivationVariables.jobId, changeJobActivationVariables.changeJobActivationDto),
    {
      useErrorBoundary: true,
    }
  );
};

export const useCreateJobProfileMutation = (): UseMutationResult<string | void, unknown, JobProfile, unknown> => {
  const { createJobProfile } = useLaborRequests();
  return useMutation((jobProfile: JobProfile) => createJobProfile(jobProfile), {
    useErrorBoundary: true,
  });
};

export const useExportPayrollMutation = (): UseMutationResult<
  ExportFileReportGridResponse | void,
  unknown,
  BaseReportDataRequest,
  unknown
> => {
  const { exportGenericPayrollData } = useLaborRequests();
  return useMutation(
    (employeeReportGridExportRequest: BaseReportDataRequest) =>
      exportGenericPayrollData(employeeReportGridExportRequest),
    {
      useErrorBoundary: true,
    }
  );
};

export const useExportEmployeeTipGridMutation = (): UseMutationResult<
  ExportFileReportGridResponse | void,
  unknown,
  BaseReportDataRequest,
  unknown
> => {
  const { exportEmployeeTipReportGridData } = useLaborRequests();
  return useMutation(
    (employeeTipReportGridRequest: BaseReportDataRequest) =>
      exportEmployeeTipReportGridData(employeeTipReportGridRequest),
    {
      useErrorBoundary: true,
    }
  );
};

export const useExportEmployeePayrollGridMutation = (): UseMutationResult<
  ExportFileReportGridResponse | void,
  IApiError,
  BaseReportDataRequest,
  unknown
> => {
  const { exportEmployeePayrollGridData } = useLaborRequests();
  return useMutation(
    (employeePayrollReportGridRequest: BaseReportDataRequest) =>
      exportEmployeePayrollGridData(employeePayrollReportGridRequest),
    {
      useErrorBoundary: true,
    }
  );
};

export const useUpdateJobProfileMutation = (): UseMutationResult<
  string | void,
  unknown,
  { jobProfileId: string; updateJobProfileDto: UpdateJobProfileDto },
  unknown
> => {
  const { updateJobProfile } = useLaborRequests();
  return useMutation(
    (updateJobProfileVariables: { jobProfileId: string; updateJobProfileDto: UpdateJobProfileDto }) =>
      updateJobProfile(updateJobProfileVariables.jobProfileId, updateJobProfileVariables.updateJobProfileDto),
    {
      useErrorBoundary: false,
    }
  );
};

export const useDeleteJobProfileMutation = (): UseMutationResult<
  string | void,
  unknown,
  {
    jobProfileId: string;
  },
  unknown
> => {
  const { deleteJobProfile } = useLaborRequests();
  return useMutation(({ jobProfileId }) => deleteJobProfile(jobProfileId), {
    useErrorBoundary: true,
  });
};

export const useCreateSelfEmployeeMutation = (): UseMutationResult<
  CreateSelfEmployeeResponse | void,
  unknown,
  CreateSelfEmployeeRequestSchema,
  unknown
> => {
  const { createSelfEmployee } = useLaborRequests();
  return useMutation((employee: CreateSelfEmployeeRequestSchema) => createSelfEmployee(employee), {
    useErrorBoundary: true,
  });
};

export const useCreateEmployeeMutation = (): UseMutationResult<
  CreateEmployeeResponse | void,
  unknown,
  CreateEmployeeRequestSchema,
  unknown
> => {
  const { createEmployee } = useLaborRequests();
  return useMutation((employee: CreateEmployeeRequestSchema) => createEmployee(employee), {
    useErrorBoundary: true,
  });
};

export const useExportEmployeePayrollDetailGridMutation = (): UseMutationResult<
  ExportFileReportGridResponse | void,
  IApiError,
  EmployeePayrollDetailExportDto,
  unknown
> => {
  const { exportEmployeePayrollDetailGridData } = useLaborRequests();
  return useMutation(
    (employeePayrollDetailExport: EmployeePayrollDetailExportDto) =>
      exportEmployeePayrollDetailGridData(employeePayrollDetailExport),
    {
      useErrorBoundary: true,
    }
  );
};

export const useSaveTipShareDistributionConfigMutation = (): UseMutationResult<
  TipShareDistributionConfig | void,
  unknown,
  TipShareDistributionConfig
> => {
  const { saveTipShareDistributionConfig } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((data: TipShareDistributionConfig) => saveTipShareDistributionConfig(data), {
    useErrorBoundary: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.tipConfiguration.all);
      queryClient.invalidateQueries(queryKeys.tipShare.distribution());
    },
  });
};

export const useUpdateTipShareDistributionConfigMutation = (): UseMutationResult<
  TipShareDistributionConfig | void,
  unknown,
  TipShareDistributionConfig
> => {
  const { updateTipShareDistributionConfig } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((data: TipShareDistributionConfig) => updateTipShareDistributionConfig(data), {
    useErrorBoundary: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.tipConfiguration.all);
      queryClient.invalidateQueries(queryKeys.tipShare.distribution());
    },
  });
};

export const useSaveTipShareContributionConfigMutation = (): UseMutationResult<
  TipShareContributionConfig | void,
  unknown,
  TipShareContributionConfig
> => {
  const { saveTipShareContributionConfig } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((data: TipShareContributionConfig) => saveTipShareContributionConfig(data), {
    useErrorBoundary: false,
    onSuccess: () => {
      // Invalidate and refetch contribution query after successful save
      queryClient.invalidateQueries(queryKeys.tipShare.contribution());
    },
  });
};

export const useUpdateTipShareContributionConfigMutation = (): UseMutationResult<
  TipShareContributionConfig | void,
  unknown,
  TipShareContributionConfig
> => {
  const { updateTipShareContributionConfig } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((data: TipShareContributionConfig) => updateTipShareContributionConfig(data), {
    useErrorBoundary: false,
    onSuccess: () => {
      // Invalidate and refetch contribution query after successful update
      queryClient.invalidateQueries(queryKeys.tipShare.contribution());
    },
  });
};

export const useSaveTipDistributionMutation = (
  autoBalance?: boolean
): UseMutationResult<SaveTipDistributionResponseDto | void, IApiError, SaveTipDistributionDto, unknown> => {
  const { saveTipDistribution } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((data: SaveTipDistributionDto) => saveTipDistribution(data, autoBalance), {
    useErrorBoundary: false,
    onSuccess: () => {
      // Refetch tip distribution data after successful save
      queryClient.invalidateQueries({ queryKey: queryKeys.tipDistribution.all });
    },
  });
};

export const useCalculateTipDistributionMutation = (): UseMutationResult<
  CalculateTipDistributionResponseDto | void,
  IApiError,
  CalculateTipDistributionRequestDto,
  unknown
> => {
  const { calculateTipDistribution } = useLaborRequests();
  return useMutation((payload: CalculateTipDistributionRequestDto) => calculateTipDistribution(payload), {
    useErrorBoundary: false,
  });
};

export const useCreateTipShareShiftMutation = (): UseMutationResult<
  TipShareShift | void,
  unknown,
  CreateTipShareShiftDto
> => {
  const { createTipShareShift } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((createDto: CreateTipShareShiftDto) => createTipShareShift(createDto), {
    useErrorBoundary: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.tipShareShift.all);
    },
  });
};

export const useUpdateTipShareShiftMutation = (): UseMutationResult<
  TipShareShift | void,
  unknown,
  { shiftId: string; updateDto: UpdateTipShareShiftDto }
> => {
  const { updateTipShareShift } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation(
    ({ shiftId, updateDto }: { shiftId: string; updateDto: UpdateTipShareShiftDto }) =>
      updateTipShareShift(shiftId, updateDto),
    {
      useErrorBoundary: false,
      onSuccess: (_, { shiftId }) => {
        queryClient.invalidateQueries(queryKeys.tipShareShift.all);
        queryClient.invalidateQueries(queryKeys.tipShareShift.detail(shiftId));
      },
    }
  );
};

export const useDeleteTipShareShiftMutation = (): UseMutationResult<void, unknown, string> => {
  const { deleteTipShareShift } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((shiftId: string) => deleteTipShareShift(shiftId), {
    useErrorBoundary: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.tipShareShift.all);
    },
  });
};

export const useDismissBannerMutation = (): UseMutationResult<
  DismissBannerResponseDto | void,
  unknown,
  DismissBannerDto
> => {
  const { dismissBanner } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((dismissDto: DismissBannerDto) => dismissBanner(dismissDto), {
    useErrorBoundary: false,
    onMutate: async (variables) => {
      const bannerStateKey = queryKeys.bannerState.byEmployeeAndType(variables.entityId, variables.bannerType);

      await queryClient.cancelQueries(bannerStateKey);

      // Snapshot the previous value for rollback on error
      const previousBannerState = queryClient.getQueryData<BannerStateViewDto>(bannerStateKey);

      queryClient.setQueryData<BannerStateViewDto | undefined>(bannerStateKey, (old: BannerStateViewDto | undefined) =>
        old ? { ...old, isVisible: false } : undefined
      );

      // Return context with snapshot for potential rollback
      return { previousBannerState };
    },
    onError: (_, variables, context) => {
      const bannerStateKey = queryKeys.bannerState.byEmployeeAndType(variables.entityId, variables.bannerType);

      // Rollback on error: restore previous state
      if (context?.previousBannerState) {
        queryClient.setQueryData(bannerStateKey, context.previousBannerState);
      }
    },
    onSuccess: (_, variables) => {
      const bannerStateKey = queryKeys.bannerState.byEmployeeAndType(variables.entityId, variables.bannerType);

      // Invalidate to refetch and confirm server state
      queryClient.invalidateQueries(bannerStateKey);
      queryClient.invalidateQueries(queryKeys.bannerState.all);
    },
  });
};

export const useFinalizeTipDistributionMutation = (): UseMutationResult<
  FinalizationResponseDto | void,
  IApiError,
  FinalizationRequestDto,
  unknown
> => {
  const { finalizeTipDistribution } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((payload: FinalizationRequestDto) => finalizeTipDistribution(payload), {
    useErrorBoundary: false,
    onSuccess: (_, { businessDate }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tipDistribution.byDate(businessDate) });
    },
  });
};

export const useUnfinalizeTipDistributionMutation = (): UseMutationResult<
  FinalizationResponseDto | void,
  IApiError,
  FinalizationRequestDto,
  unknown
> => {
  const { unfinalizeTipDistribution } = useLaborRequests();
  const queryClient = useQueryClient();
  const queryKeys = useLaborQueryKeys();

  return useMutation((payload: FinalizationRequestDto) => unfinalizeTipDistribution(payload), {
    useErrorBoundary: false,
    onSuccess: (_, { businessDate }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tipDistribution.byDate(businessDate) });
    },
  });
};
